import React, { FC, RefObject, useContext } from 'react';
import { Grid } from '@mui/material';
import styled from 'styled-components';
import { ScrollableGrid } from 'spw-shared/globalComponents';
import {
  ISpaceBreakDown,
  UpdateSpacesByTypeParams,
} from 'spw-redux/modules/program-summary/types';
import {
  IProject,
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { ProgramSummaryCardsContext } from 'spw-esa-projects/program-summary/summary-cards';
import { SpaceRatioCard } from './space-ratio-card';
import { WorkspaceUsfMix } from './workspace-usf-mix';

const PaddedGrid = styled(Grid)`
  && {
    padding: 8px;
  }
`;

export interface CategorySummaryRowProps {
  innerRef?: RefObject<HTMLDivElement>;
  currentProject: IProject;
  categorySpacesBreakdown: ISpaceBreakDown[];
  onClickSpaceIcon?: (e: Element) => void;
  projectColors: IProjectColors;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  leftGridDataTestId: string;
  rightGridDataTestId: string;
  spaceTypeCategory: IProjectSpaceTypeCategory;
  viewOnly: boolean;
  rightSubtitle?: string;
  leftSubtitle?: string;
  onUpdateAvg?: (payload: UpdateSpacesByTypeParams) => void;
  clientFacingNamesAreVisible: boolean;
}

export const CategorySummaryRow: FC<CategorySummaryRowProps> = ({
  innerRef,
  currentProject,
  categorySpacesBreakdown,
  onClickSpaceIcon,
  projectColors,
  projectSpaceTypeCategories,
  projectSpaceTypes,
  leftGridDataTestId,
  rightGridDataTestId,
  spaceTypeCategory,
  onUpdateAvg,
  viewOnly,
  clientFacingNamesAreVisible,
}) => {
  const { getUnit } = useUnitConversion();
  const leftTitle = `${spaceTypeCategory?.name} ${getUnit('area')} MIX`;
  const rightTitle = `${spaceTypeCategory?.name} RATIOS`;
  const { visibleCards, isRenderingForExport } = useContext(
    ProgramSummaryCardsContext
  );

  const categoryUsfAndSeatMix =
    visibleCards.categoryUsfAndSeatMix[
      spaceTypeCategory.defaultSpaceTypeCategory.category
    ];

  return (
    <>
      <ScrollableGrid container ref={innerRef} $scrollMarginTop={132}>
        {(!isRenderingForExport ||
          categoryUsfAndSeatMix.usfMixAndRatios ||
          categoryUsfAndSeatMix.usfMix) && (
          <PaddedGrid
            item
            xs={6}
            className="mr-16"
            data-testid={leftGridDataTestId}
          >
            <WorkspaceUsfMix
              viewOnly={viewOnly}
              projectType={currentProject.type}
              workspacesBreakdown={categorySpacesBreakdown}
              title={leftTitle}
              onClickSpaceIcon={onClickSpaceIcon}
              projectColors={projectColors}
              projectSpaceTypes={projectSpaceTypes}
              projectSpaceTypeCategories={projectSpaceTypeCategories}
              spaceTypeCategory={spaceTypeCategory}
              onUpdateAvgUsf={onUpdateAvg}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          </PaddedGrid>
        )}

        {(!isRenderingForExport ||
          categoryUsfAndSeatMix.usfMixAndRatios ||
          categoryUsfAndSeatMix.ratios) && (
          <PaddedGrid item xs={6} data-testid={rightGridDataTestId}>
            <SpaceRatioCard
              viewOnly={viewOnly}
              title={rightTitle}
              onClickSpaceIcon={onClickSpaceIcon}
              projectSpaceTypes={projectSpaceTypes}
              projectSpaceTypeCategories={projectSpaceTypeCategories}
              projectColors={projectColors}
              spacesBreakdown={categorySpacesBreakdown}
              spaceTypeCategory={spaceTypeCategory}
              onUpdateAvgSeatCount={onUpdateAvg}
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          </PaddedGrid>
        )}
      </ScrollableGrid>
    </>
  );
};
